'use client';

import { FC, memo } from 'react';
import { HEADER_PHRASES } from 'consts/phrases/headerPhrases';
import { Button } from '@mui/material';
import { getMenuItems, getAuthMenuItems } from '../menuUtils';
import { IBadgeDetails } from '@/store/accountStore';
import { MenuNotificationBadge } from '@/components/blocks/header/MenuNotificationBadge/MenuNotificationBadge';
import { Link } from '@/components/general/Link/Link';
import { LangSwitcher } from '../LangSwitcher/LangSwitcher';
import { IPhrases } from '@/types/types/page/pages';
import { usePropsPhrases } from '@/utils/phrases/usePropsPhrases';
import { useCurrentPath } from '@/hooks/general/useCurrentPath/useCurrentPath';
import { Icon } from '@/components/general/Icon/Icon';
import { useAuthMenuItemsByAccount } from '../useAuthMenuItemsByAccount';

import styles from '@/styles/modules/TopMenu.module.css';

interface TopMenuProps {
    phrases?: IPhrases;
    isLoggedIn: boolean;
    handleLogout: () => Promise<void>;
    badgeDetails: string;
    blogUrl?: string;
    faqUrl?: string;
    contactUrl?: string;
    helpCentreUrl?: string;
}

export const TopMenu: FC<TopMenuProps> = ({
    phrases,
    isLoggedIn,
    handleLogout,
    badgeDetails,
    blogUrl,
    faqUrl,
    contactUrl,
    helpCentreUrl,
}) => {
    const { t } = usePropsPhrases(phrases);
    const { pathname } = useCurrentPath();

    const menuItems = getMenuItems(blogUrl, faqUrl, contactUrl, helpCentreUrl);
    const authMenuItems = getAuthMenuItems(JSON.parse(badgeDetails) as IBadgeDetails);

    const { authMenuItemsByAccount } = useAuthMenuItemsByAccount({ authMenuItems });

    return (
        <nav className={styles.menuBlock}>
            {isLoggedIn ? (
                <>
                    <ul className={`${styles.topmenuLinks} ${styles.topmenu} topMenuLinksList`}>
                        {authMenuItemsByAccount.map((menuItem) => (
                            <li key={menuItem.phraseId}>
                                <Link href={menuItem.href}>
                                    <Button
                                        className={`topMenuButton ${
                                            pathname.startsWith(menuItem?.basePath ?? menuItem.href)
                                                ? `active`
                                                : ''
                                        } ${menuItem.className ?? ''}`}
                                        variant="tertiary"
                                        size="small"
                                        tabIndex={-1}
                                    >
                                        {menuItem.icon}
                                        <MenuNotificationBadge data={menuItem.data}>
                                            {t(menuItem.phraseId)}
                                        </MenuNotificationBadge>
                                    </Button>
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <ul className={styles.topmenu}>
                        <li>
                            <LangSwitcher />
                        </li>
                        <li>
                            <Button
                                onClick={handleLogout}
                                className={`logoutButton ${styles.topmenuButton}`}
                                size="small"
                                variant="outlined"
                            >
                                <Icon
                                    path="general/navigationIcons/LogoutIcon"
                                    className={`${styles.topmenuButtonIcon} topmenuButtonIcon`}
                                />
                                {t(HEADER_PHRASES.LOGOUT)}
                            </Button>
                        </li>
                    </ul>
                </>
            ) : (
                <>
                    <ul className={`${styles.topmenuLinks} ${styles.topmenu} topMenuLinksList`}>
                        {menuItems.map(
                            (menuItem) =>
                                !menuItem.invisible && (
                                    <li key={menuItem.phraseId}>
                                        <Link
                                            href={menuItem.href}
                                            target={menuItem.openInNewTab ? '_blank' : '_self'}
                                        >
                                            <Button
                                                className={`topMenuButton ${
                                                    pathname.startsWith(menuItem.href)
                                                        ? `${styles.active} active`
                                                        : ''
                                                }`}
                                                disableRipple
                                                variant="tertiary"
                                                size="small"
                                                tabIndex={-1}
                                            >
                                                {t(menuItem.phraseId)}
                                            </Button>
                                        </Link>
                                    </li>
                                ),
                        )}
                    </ul>
                    <ul className={styles.topmenu}>
                        <li>
                            <LangSwitcher />
                        </li>
                        <li>
                            <Link href="/login" passHref className={styles.topmenuButtonLink}>
                                <Button
                                    variant="outlined"
                                    className={`loginButton ${styles.topmenuButton}`}
                                    tabIndex={-1}
                                >
                                    <Icon
                                        path="general/navigationIcons/LoginIcon"
                                        className={`${styles.topmenuButtonIcon} topmenuButtonIcon`}
                                    />
                                    {t(HEADER_PHRASES.LOGIN)}
                                </Button>
                            </Link>
                        </li>
                    </ul>
                </>
            )}
        </nav>
    );
};

export const MemoizedTopMenu = memo(TopMenu);
